/**
 * NosAKDataWorkspace — /nosak/workspace
 * Bloomberg-style SSOT operating workspace.
 * Auto-loads first subsidiary. Tabbed inputs. Live right panel.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNosak } from "@/context/NOSAKContext";
import client from "@/api/client";
import { toast } from "sonner";
import { Save, RefreshCw, Activity, Shield, CheckCircle2, AlertTriangle, Database } from "lucide-react";

const fmtN = (v, u = "") => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M ${u}`.trim(); if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K ${u}`.trim(); return `${n.toLocaleString()} ${u}`.trim(); };
const fmtU = (v) => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`; if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`; return `$${n}`; };

const DIV_COLOR = { distilleries: "#d97706", agro_industrial: "#059669", agriculture: "#10b981", chemicals: "#6366f1", logistics: "#94a3b8", packaging: "#0891b2" };

// Numeric input
const N = ({ label, name, unit = "", value, onChange }) => (
  <div>
    <p className="text-[10px] text-slate-500 mb-1 font-medium">{label}{unit ? ` (${unit})` : ""}</p>
    <input type="number" min={0} value={value || ""} onChange={e => onChange(name, e.target.value)}
      className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#0f172a]"
      data-testid={`input-${name}`} />
  </div>
);

const TABS = [
  { id: "s1", label: "Scope 1", sub: "Direct combustion" },
  { id: "s2", label: "Scope 2", sub: "Grid electricity" },
  { id: "s3", label: "Scope 3", sub: "Value chain" },
  { id: "circ", label: "Circularity", sub: "Waste & biomass" },
  { id: "agri", label: "Agriculture", sub: "Palm & cassava" },
  { id: "finance", label: "Financial", sub: "Projects & CAPEX" },
];

export default function NosAKDataWorkspace() {
  const { subsidiaries, loading, reload, selectedPeriod } = useNosak();
  const [activeSub, setActiveSub] = useState(null);
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState(false);
  const [liveCalc, setLiveCalc] = useState(null);
  const [loadingSub, setLoadingSub] = useState(false);
  const [activeTab, setActiveTab] = useState("s1");
  const [propagationLog, setPropagationLog] = useState([]);

  // Auto-load first subsidiary
  const loadSubsidiary = useCallback(async (sub) => {
    if (!sub || !selectedPeriod) return;
    setLoadingSub(true);
    try {
      const r = await client.get(`/nosak/subsidiary/${sub.sub_id}/${selectedPeriod}`);
      const full = r.data;
      setActiveSub({ ...sub, ...full });
      setInputs({
        diesel_litres: full.diesel_litres || 0, hfo_tonnes: full.hfo_tonnes || 0,
        natural_gas_m3: full.natural_gas_m3 || 0, boiler_fuel_litres: full.boiler_fuel_litres || 0,
        fleet_diesel_litres: full.fleet_diesel_litres || 0, process_emissions_tco2e: full.process_emissions_tco2e || 0,
        electricity_kwh: full.electricity_kwh || 0, irrigation_kwh: full.irrigation_kwh || 0,
        logistics_km: full.logistics_km || 0, upstream_agri_t: full.upstream_agri_t || 0,
        packaging_t: full.packaging_t || 0,
        waste_generated_t: full.waste_generated_t || 0, waste_recycled_t: full.waste_recycled_t || 0,
        biomass_recovered_t: full.biomass_recovered_t || 0, energy_recovered_kwh: full.energy_recovered_kwh || 0,
        packaging_recycled_pct: full.packaging_recycled_pct || 0,
        palm_ffb_t: full.palm_ffb_t || 0, cassava_t: full.cassava_t || 0,
        fertilizer_n_kg: full.fertilizer_n_kg || 0,
        production_output: full.production_output || 0,
        capex_investment_usd: full.capex_investment_usd || 0,
        evidence_level: full.evidence_level || "estimated",
      });
      setLiveCalc(full.calc || null);
    } catch { toast.error("Failed to load subsidiary"); }
    finally { setLoadingSub(false); }
  }, [selectedPeriod]);

  useEffect(() => {
    if (!loading && subsidiaries.length > 0 && !activeSub) {
      loadSubsidiary(subsidiaries[0]);
    }
  }, [loading, subsidiaries, activeSub, loadSubsidiary]);

  const chg = (name, val) => setInputs(prev => ({ ...prev, [name]: parseFloat(val) || 0 }));

  const save = async () => {
    if (!activeSub) return;
    setSaving(true);
    try {
      const r = await client.post(`/nosak/subsidiary/${activeSub.sub_id}/${selectedPeriod}/calculate`, inputs);
      setLiveCalc(r.data.calc);
      // Show propagation log
      const now = new Date().toLocaleTimeString();
      setPropagationLog([
        { step: "Data Workspace", status: "Updated", time: now },
        { step: "Dashboard",      status: "Recalculated", time: now },
        { step: "Finance",        status: "Updated", time: now },
        { step: "ESG & Risk",     status: "Updated", time: now },
        { step: "Reports",        status: "Ready",   time: now },
      ]);
      toast.success("SSOT updated — propagating platform-wide");
      reload();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const calc = liveCalc || activeSub?.calc;

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="p-6 lg:p-8 bg-[#0d1117] min-h-screen">

      {/* Top Banner */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 bg-emerald-900/50 border border-emerald-700/50 rounded-lg flex items-center justify-center">
            <Database size={14} className="text-emerald-400" />
          </div>
          <div>
            <h1 className="text-base font-black text-white">Climate & Circularity Data Workspace</h1>
            <p className="text-[10px] text-slate-500">One input → one calculation → propagates platform-wide · {selectedPeriod}</p>
          </div>
        </div>
        <div className="bg-emerald-900/20 border border-emerald-800/30 rounded-lg px-4 py-2 text-[10px] text-emerald-400 flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          SSOT ARCHITECTURE ACTIVE — All downstream pages (Dashboard, Intelligence, Finance, ESG) read from this workspace.
        </div>
      </div>

      <div className="flex gap-4">

        {/* Left Sidebar — Subsidiary Selector */}
        <div className="w-56 flex-shrink-0 space-y-1.5">
          <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest px-1 mb-2">Select Entity</p>
          {subsidiaries.map(sub => {
            const isActive = activeSub?.sub_id === sub.sub_id;
            const conf = sub.data_confidence_score || sub.calc?.data_confidence_score || 45;
            const circ = sub.circularity_score || sub.calc?.circularity_score || 0;
            return (
              <button key={sub.sub_id} onClick={() => loadSubsidiary(sub)}
                className={`w-full text-left rounded-xl border p-3 transition-all ${
                  isActive ? "bg-slate-800 border-slate-600" : "bg-slate-900 border-slate-800 hover:border-slate-600"
                }`}
                data-testid={`sub-select-${sub.sub_id}`}>
                <p className="text-[11px] font-bold text-white truncate">{sub.name}</p>
                <p className="text-[9px] capitalize" style={{ color: DIV_COLOR[sub.division] || "#94a3b8" }}>
                  {(sub.division || "").replace(/_/g, " ")}
                </p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[9px] text-slate-400">
                    {fmtN(sub.total_tco2e || sub.calc?.total_tco2e || 0)} tCO₂e
                  </span>
                  <div className="flex items-center gap-1">
                    <div className="w-8 h-1 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: circ + "%" }} />
                    </div>
                    <span className={`text-[8px] font-bold ${conf >= 65 ? "text-emerald-400" : conf >= 45 ? "text-amber-400" : "text-red-400"}`}>
                      {conf >= 65 ? "T2" : "T1"}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Main Workspace */}
        <div className="flex-1 min-w-0">
          {loadingSub ? (
            <div className="bg-slate-900 rounded-xl border border-slate-800 flex items-center justify-center h-64">
              <div className="text-center"><RefreshCw size={16} className="animate-spin text-slate-500 mx-auto mb-2" /><p className="text-xs text-slate-500">Loading subsidiary data…</p></div>
            </div>
          ) : activeSub ? (
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
              {/* Sub header */}
              <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                <div>
                  <p className="text-sm font-black text-white">{activeSub.name}</p>
                  <p className="text-[10px] text-slate-500">{activeSub.location} · {activeSub.sector?.replace(/_/g, " ")}</p>
                </div>
                <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 bg-emerald-700 text-white text-xs px-4 py-2 rounded-lg font-bold hover:bg-emerald-600 disabled:opacity-60"
                  data-testid="save-calculate-btn">
                  {saving ? <><RefreshCw size={11} className="animate-spin" /> Calculating…</> : <><Save size={11} /> Save & Calculate</>}
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-slate-800 overflow-x-auto">
                {TABS.map(t => (
                  <button key={t.id} onClick={() => setActiveTab(t.id)}
                    className={`flex-shrink-0 px-4 py-3 text-[11px] font-medium transition-colors ${
                      activeTab === t.id ? "text-white border-b-2 border-emerald-500" : "text-slate-500 hover:text-slate-300"
                    }`}
                    data-testid={`tab-${t.id}`}>
                    {t.label}
                    <span className="block text-[9px] opacity-60">{t.sub}</span>
                  </button>
                ))}
              </div>

              {/* Input content */}
              <div className="p-4">
                {activeTab === "s1" && (
                  <div className="grid grid-cols-3 gap-3">
                    <N label="Diesel" name="diesel_litres" unit="litres" value={inputs.diesel_litres} onChange={chg} />
                    <N label="HFO (Heavy Fuel Oil)" name="hfo_tonnes" unit="tonnes" value={inputs.hfo_tonnes} onChange={chg} />
                    <N label="Natural Gas" name="natural_gas_m3" unit="m³" value={inputs.natural_gas_m3} onChange={chg} />
                    <N label="Boiler Fuel" name="boiler_fuel_litres" unit="litres" value={inputs.boiler_fuel_litres} onChange={chg} />
                    <N label="Fleet Diesel" name="fleet_diesel_litres" unit="litres" value={inputs.fleet_diesel_litres} onChange={chg} />
                    <N label="Process Emissions" name="process_emissions_tco2e" unit="tCO₂e" value={inputs.process_emissions_tco2e} onChange={chg} />
                  </div>
                )}
                {activeTab === "s2" && (
                  <div className="grid grid-cols-3 gap-3">
                    <N label="Grid Electricity" name="electricity_kwh" unit="kWh" value={inputs.electricity_kwh} onChange={chg} />
                    <N label="Irrigation Electricity" name="irrigation_kwh" unit="kWh" value={inputs.irrigation_kwh} onChange={chg} />
                    <div className="col-span-3 mt-2 p-3 bg-slate-800 rounded-lg">
                      <p className="text-[9px] text-slate-400">Grid emission factor: 0.43 kgCO₂e/kWh (Nigeria NERC 2022). Scope 2 = electricity × EF / 1,000.</p>
                    </div>
                  </div>
                )}
                {activeTab === "s3" && (
                  <div className="grid grid-cols-3 gap-3">
                    <N label="Logistics Distance" name="logistics_km" unit="km" value={inputs.logistics_km} onChange={chg} />
                    <N label="Upstream Agri Inputs" name="upstream_agri_t" unit="tonnes" value={inputs.upstream_agri_t} onChange={chg} />
                    <N label="Packaging Material" name="packaging_t" unit="tonnes" value={inputs.packaging_t} onChange={chg} />
                    <div className="col-span-3 mt-2 p-3 bg-slate-800 rounded-lg">
                      <p className="text-[9px] text-slate-400">Scope 3 EFs: Logistics 0.21 kgCO₂e/km · Agri inputs 0.5 tCO₂e/t · Packaging 1.2 tCO₂e/t</p>
                    </div>
                  </div>
                )}
                {activeTab === "circ" && (
                  <div className="grid grid-cols-3 gap-3">
                    <N label="Waste Generated" name="waste_generated_t" unit="tonnes" value={inputs.waste_generated_t} onChange={chg} />
                    <N label="Waste Recycled" name="waste_recycled_t" unit="tonnes" value={inputs.waste_recycled_t} onChange={chg} />
                    <N label="Biomass Recovered" name="biomass_recovered_t" unit="tonnes" value={inputs.biomass_recovered_t} onChange={chg} />
                    <N label="Energy Recovered" name="energy_recovered_kwh" unit="kWh" value={inputs.energy_recovered_kwh} onChange={chg} />
                    <N label="Packaging Recycled %" name="packaging_recycled_pct" unit="%" value={inputs.packaging_recycled_pct} onChange={chg} />
                    <N label="Production Output" name="production_output" value={inputs.production_output} onChange={chg} />
                  </div>
                )}
                {activeTab === "agri" && (
                  <div className="grid grid-cols-3 gap-3">
                    <N label="Palm FFB" name="palm_ffb_t" unit="tonnes" value={inputs.palm_ffb_t} onChange={chg} />
                    <N label="Cassava Output" name="cassava_t" unit="tonnes" value={inputs.cassava_t} onChange={chg} />
                    <N label="Fertilizer N Applied" name="fertilizer_n_kg" unit="kg N" value={inputs.fertilizer_n_kg} onChange={chg} />
                    <div className="col-span-3 mt-2 p-3 bg-slate-800 rounded-lg">
                      <p className="text-[9px] text-slate-400">N₂O EF: 4.5 kgCO₂e/kg N (IPCC Tier 1). POME estimated at 0.67m³/t FFB — POME methane opportunity.</p>
                    </div>
                  </div>
                )}
                {activeTab === "finance" && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <N label="CAPEX Investment" name="capex_investment_usd" unit="USD" value={inputs.capex_investment_usd} onChange={chg} />
                    </div>
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-[10px] font-bold text-slate-300 mb-1">Evidence Level</p>
                      <select value={inputs.evidence_level} onChange={e => setInputs(p => ({ ...p, evidence_level: e.target.value }))}
                        className="text-xs border border-slate-700 rounded-lg px-3 py-1.5 bg-slate-900 text-white w-full max-w-[200px]"
                        data-testid="evidence-level-select">
                        <option value="estimated">Estimated (T1)</option>
                        <option value="declared">Declared (T2)</option>
                        <option value="measured">Measured (T2+)</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Right Sticky Panel — Live SSOT Output */}
        <div className="w-52 flex-shrink-0">
          <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-4 sticky top-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Live SSOT Output</p>
            </div>
            {!calc ? (
              <p className="text-[10px] text-slate-600">Select a subsidiary to see live outputs</p>
            ) : (
              <div className="space-y-2">
                {[
                  ["Scope 1", fmtN(calc.scope1?.total, "tCO₂e"), "#f87171"],
                  ["Scope 2", fmtN(calc.scope2?.total, "tCO₂e"), "#fbbf24"],
                  ["Scope 3", fmtN(calc.scope3?.total, "tCO₂e"), "#94a3b8"],
                  ["Total", fmtN(calc.total_tco2e, "tCO₂e/yr"), "#f8fafc"],
                  ["Circularity", (calc.circularity_score || 0).toFixed(0) + "/100", "#34d399"],
                  ["Reduction", fmtN(calc.reduction_potential_tco2e), "#34d399"],
                  ["Monetisable", fmtN(calc.carbon_qualification?.monetisable || 0), "#fbbf24"],
                  ["Base Carbon", fmtU(calc.carbon_qualification?.carbon_value?.base || 0) + "/yr", "#fbbf24"],
                  ["Confidence", (calc.data_confidence_score || 0) + "/100", "#94a3b8"],
                ].map(([k, v, c]) => (
                  <div key={k} className="flex justify-between items-center py-1 border-b border-white/5 last:border-0">
                    <span className="text-[10px] text-slate-500">{k}</span>
                    <span className="text-[10px] font-black" style={{ color: c }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
            {propagationLog.length > 0 && (
              <div className="mt-4 border-t border-white/10 pt-3">
                <p className="text-[9px] font-bold text-emerald-400 uppercase tracking-widest mb-2">Propagated to</p>
                {propagationLog.map((l, i) => (
                  <div key={i} className="flex items-center gap-2 py-1 text-[9px]">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span className="text-slate-400 flex-1">{l.step}</span>
                    <span className="text-emerald-400 font-medium">{l.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
